/**
 * The Google Photos window and the photos' bytes: opening the one window a
 * tap may open (a window over the app on a computer, a tab on a phone),
 * the "Opening Google Photos…" line in it while the session is made,
 * closing it, waiting between asks (woken early when the gardener comes
 * back to this tab), and fetching each picked photo through our own server.
 *
 * Moved word for word out of GooglePhotosService (W1.18 part 3, 2026-10-26),
 * which calls these from its pick; the sign-in's wait is in
 * googlePhotosSignIn, the shared types and the Sentry report in
 * googlePhotosSession.
 *
 * Requests: POST /google-photos/media { baseUrl, accessToken } → the image
 * (a Blob), through apiClient (Firebase token, and X-Grow-Garden). Google
 * does not promise CORS headers on a `baseUrl`, so the bytes come through
 * routes/googlePhotos.js in the backend.
 *
 * @module googlePhotosPicker
 */

import { debugLog } from '@/config/config';
import { apiClient } from '@/shared/api/apiClient';
import { t } from '@/shared/i18n/i18nSetup';
import { GOOGLE_PHOTOS_WINDOW_NAME } from '@/config/tunables/shared';
import { GooglePhotosError, takenAtOf, type PickedMediaItem } from './googlePhotosFlow';
import type { GooglePhotoPick } from './googlePhotosSession';

/**
 * Opens the Google Photos window, inside the tap.
 *
 * A computer gets a window over the app, sized for the picker; a phone
 * ignores the size and opens a tab. Named, so a second tap reuses the window
 * rather than opening another. Not `noopener`: a browser answers that with
 * null whether or not the window opened, which read as blocked.
 *
 * @param url - Where it opens.
 * @returns The window, or null when the browser blocked it.
 */
export function openGoogleWindow(url: string): Window | null {
  const width = Math.min(1000, window.screen?.availWidth || 1000);
  const height = Math.min(760, window.screen?.availHeight || 760);
  const left = Math.max(0, (window.screenX || 0) + ((window.outerWidth || width) - width) / 2);
  const top = Math.max(0, (window.screenY || 0) + ((window.outerHeight || height) - height) / 2);
  return window.open(url, GOOGLE_PHOTOS_WINDOW_NAME, `popup,width=${width},height=${height},left=${left},top=${top}`);
}

/**
 * Puts a line in the blank window while the session is made, so it is not
 * an empty page. Quiet where the window cannot be written to.
 *
 * @param win - The window.
 */
export function showOpening(win: Window): void {
  try {
    win.document.title = t('plants.photo.source.google');
    const line = win.document.createElement('p');
    line.textContent = t('plants.photo.source.googleOpening');
    line.style.cssText = 'font:16px system-ui,sans-serif;color:#2d5a27;text-align:center;margin-top:40vh';
    win.document.body.replaceChildren(line);
  } catch { /* not ours to write in */ }
}

/**
 * Closes a window if it can; one Google has cut off cannot be, and closes
 * itself on Done.
 *
 * @param win - The window.
 */
export function closeQuietly(win: Window | null): void {
  try {
    if (win && !win.closed) win.close();
  } catch { /* cut off */ }
}

/**
 * Waits, and stops early when the gardener comes back to this tab: on a
 * phone the picker is in front, and the moment they return is the moment
 * to ask Google.
 *
 * @param ms - The longest to wait.
 * @param signal - Cancels the wait.
 * @returns When the time is up or the tab is back in front.
 * @throws GooglePhotosError 'cancelled' when cancelled.
 */
export function sleepUntilBack(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    /** Stops listening. */
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
      signal?.removeEventListener('abort', onAbort);
    };
    /** Time's up, or the gardener is back. */
    const done = () => { cleanup(); resolve(); };
    /** The tab is in front again. */
    const onBack = () => { if (document.visibilityState === 'visible') done(); };
    /** Cancelled. */
    const onAbort = () => { cleanup(); reject(new GooglePhotosError('cancelled', 'The pick was cancelled.')); };

    if (signal?.aborted) {
      reject(new GooglePhotosError('cancelled', 'The pick was cancelled.'));
      return;
    }
    const timer = setTimeout(done, ms);
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Fetches the bytes, through our own server.
 *
 * @param item - The picked photo.
 * @param token - The token the session was made with.
 * @param signal - Cancels the download.
 * @returns The photo as a file, the day Google says it was taken, and Google's id for the item.
 */
export async function download(item: PickedMediaItem, token: string, signal: AbortSignal): Promise<GooglePhotoPick> {
  const baseUrl = item.mediaFile?.baseUrl;
  if (!baseUrl) {
    throw new GooglePhotosError('download-failed', 'Google described a photo with no address.');
  }

  try {
    const response = await apiClient.post<Blob>(
      '/google-photos/media',
      { baseUrl, accessToken: token },
      { responseType: 'blob', signal },
    );
    const blob = response.data;
    const name = item.mediaFile?.filename || 'google-photo.jpg';
    const file = new File([blob], name, { type: blob.type || item.mediaFile?.mimeType || 'image/jpeg' });
    return { file, takenAt: takenAtOf(item), googleItemId: item.id };
  } catch (error) {
    debugLog('useGooglePhotos', 'Download failed', error);
    if (signal.aborted) throw new GooglePhotosError('cancelled', 'The pick was cancelled.');
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    throw new GooglePhotosError(
      'download-failed',
      `The photo was chosen but could not be fetched from Google (our server said ${status ?? 'nothing'}).`,
    );
  }
}
